import { useState } from 'react';
import LogoZoomPopover from '@/components/LogoZoomPopover';

/**
 * CompanyLogo — Round 92o++++++++k (Feb 2026)
 *
 * Larger version of CompanyIcon for use inside the About / Fund
 * Overview section header. Same self-managed loaded/errored state
 * so the component vanishes silently on failure (no broken
 * placeholder).
 *
 * Massive's logo_url is typically an SVG (Apple's example) which
 * scales cleanly at any size — we render at 40×40 inside the About
 * panel header to balance against the existing industry/HQ chips on
 * the right.
 *
 * Feb 2026 — `zoomable` prop: when set, hover shows a zoom-in
 * cursor and click opens a 280px preview near the pointer.
 */
export default function CompanyLogo({ ticker, size = 40, className = '', zoomable = false }) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const [zoomAnchor, setZoomAnchor] = useState(null);

  if (!ticker || errored) return null;

  const src = `${process.env.REACT_APP_BACKEND_URL}/api/stocks/${encodeURIComponent(ticker.toUpperCase())}/logo`;

  return (
    <>
      <img
        src={src}
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        width={size}
        height={size}
        data-testid="modal-company-logo"
        className={`rounded-md shrink-0 bg-white/90 dark:bg-white/95 p-1 transition-opacity duration-200 ${zoomable ? 'cursor-zoom-in hover:ring-2 hover:ring-violet-400/60' : ''} ${className}`}
        style={{
          width: size,
          height: size,
          objectFit: 'contain',
          opacity: loaded ? 1 : 0,
        }}
        onLoad={() => setLoaded(true)}
        onError={() => setErrored(true)}
        onClick={zoomable ? (e) => { e.stopPropagation(); setZoomAnchor({ x: e.clientX, y: e.clientY }); } : undefined}
        title={zoomable ? `Click to enlarge ${ticker} logo` : undefined}
      />
      {zoomable && zoomAnchor && (
        <LogoZoomPopover
          src={src}
          ticker={ticker}
          anchor={zoomAnchor}
          onClose={() => setZoomAnchor(null)}
        />
      )}
    </>
  );
}
